import {ofType} from 'redux-observable';
import {Observable, from, defer, of} from 'rxjs';
import {flatMap, map} from 'rxjs/operators';
import {List} from 'immutable';

import {combineEpics} from 'web-app/util/redux-observable';
import * as BillPayerActions from 'web-app/react/entities/bill-payers/actions';
import * as ChildActions from 'web-app/react/entities/children/actions';
import {replayOnTriggerObservable} from 'web-app/util/epics-helpers';
import * as AccountItemActions from 'web-app/react/entities/account-items/actions';
import * as AccountItemMoveActions from 'web-app/react/modals-container/account-item-move/actions';
import * as InvoiceModalActions from 'web-app/react/modals-container/invoice/actions';
import {chunkArray} from 'web-app/util/array';
import {PARALLEL_QUEUE_STRATEGY} from 'web-app/setup/prototype-extensions/rxjs-extensions';
import * as RecurringPaymentActions from 'web-app/react/entities/recurring-payments/actions';

import * as Api from './api';
import * as Actions from './actions';

const fetchOverviewData = action$ =>
    action$
        .ofType(Actions.fetchOverviewData.type)
        .mergeMap(({payload}: ReturnType<typeof Actions.fetchOverviewData.action>) =>
            Api.fetchDebtReportOverview(
                payload.institutionId,
                payload.asOf,
                payload.includeCreditNotesAndCancelledInvoicesInBalance,
            )
                .map(debtOverview => Actions.fetchOverviewDataSuccess.action({debtOverview}))
                .catch(e => Observable.of(Actions.fetchOverviewDataFailed.action(e))),
        );

type TriggerUpdateInput = {
    billPayerIds: string[];
    expectedPaymentIds?: string[];
    unexpectedPaymentIds?: string[];
    expectedInvoiceIds?: string[];
    unexpectedInvoiceIds?: string[];
};

const listenForUpdates = (action$, state$) => {
    const replayObservable: Observable<any> = Observable.combineLatest(
        action$.ofType(Actions.fetchOverviewData.type),
        action$.ofType(Actions.fetchBalanceData.type),
    );
    const resetObservable = action$.ofType(Actions.showingInstitutionId.type);

    const triggerReplayObservable = Observable.merge<TriggerUpdateInput[]>(
        action$.ofType(AccountItemActions.createPaymentSuccess.type, AccountItemActions.updatePaymentSuccess.type).pipe(
            map((action: any): TriggerUpdateInput[] => [
                {
                    billPayerIds: [action.payload.billPayerId],
                    expectedPaymentIds: [action.payload.newPayment.id],
                },
            ]),
        ),
        action$
            .ofType(AccountItemActions.deletePaymentSuccess.type, RecurringPaymentActions.denyPaymentSuccess.type)
            .pipe(
                map((action: any): TriggerUpdateInput[] => [
                    {
                        billPayerIds: [action.payload.billPayerId],
                        unexpectedPaymentIds: [action.payload.oldPayment.id],
                    },
                ]),
            ),
        action$.ofType(AccountItemMoveActions.movePaymentSuccess.type).pipe(
            map((action: any): TriggerUpdateInput[] => {
                const {oldBillPayerId, billPayerId, id} = action.payload;

                return [
                    {
                        billPayerIds: [oldBillPayerId],
                        unexpectedPaymentIds: [id],
                    },
                    {
                        billPayerIds: [billPayerId],
                        expectedPaymentIds: [id],
                    },
                ];
            }),
        ),
        action$
            .ofType(
                AccountItemActions.createInvoiceSuccess.type,
                InvoiceModalActions.cancelSuccess.type,
                AccountItemActions.createInvoiceWithGraphQLSuccess.type,
            )
            .pipe(
                map((action: any): TriggerUpdateInput[] => {
                    const {billPayerId, invoices} = action.payload;

                    return [
                        {
                            billPayerIds: [billPayerId],
                            expectedInvoiceIds: invoices.map((inv: any) => inv.id),
                        },
                    ];
                }),
            ),
        action$.ofType(AccountItemMoveActions.moveInvoiceSuccess.type).pipe(
            map((action: any): TriggerUpdateInput[] => {
                const {oldBillPayerId, invoice} = action.payload;

                return [
                    {
                        billPayerIds: [oldBillPayerId],
                        unexpectedInvoiceIds: [invoice.id],
                    },
                    {
                        billPayerIds: [invoice.billPayerId],
                        expectedInvoiceIds: [invoice.id],
                    },
                ];
            }),
        ),
    );

    const predicate = replayActions =>
        state$.value.plans.debtReportV2.institutionId === replayActions[0].payload.institutionId;

    return replayOnTriggerObservable(
        replayObservable,
        resetObservable,
        triggerReplayObservable,
        predicate,
        (replayedAction, triggerUpdateObjects) => {
            switch (replayedAction.type) {
                case Actions.fetchOverviewData.type:
                    return replayedAction;
                case Actions.fetchBalanceData.type: {
                    const {asOf, includeCreditNotesAndCancelledInvoicesInBalance, institutionId} =
                        replayedAction.payload;

                    return (
                        triggerUpdateObjects?.map(object => {
                            const {
                                billPayerIds,
                                expectedPaymentIds,
                                unexpectedPaymentIds,
                                expectedInvoiceIds,
                                unexpectedInvoiceIds,
                            } = object;

                            return Actions.fetchBalanceData.action({
                                institutionId,
                                asOf,
                                includeCreditNotesAndCancelledInvoicesInBalance,
                                billPayerIds,
                                expectedPaymentIds,
                                unexpectedPaymentIds,
                                expectedInvoiceIds,
                                unexpectedInvoiceIds,
                            });
                        }) ?? []
                    );
                }
                default:
                    return [];
            }
        },
    ).catch(() => {
        // This is intentionally blank since the error handling is done in whatever handles the replay actions
        return Observable.empty();
    });
};

const fetchData = action$ =>
    action$.pipe(
        ofType(Actions.fetchData.type),
        flatMap(({payload}: ReturnType<typeof Actions.fetchData.action>) => {
            const {institutionId, asOf, includeCreditNotesAndCancelledInvoicesInBalance} = payload;

            return from(Api.fetchAllBillPayers(institutionId)).pipe(
                flatMap(result => {
                    const billPayerIds = result.billPayers.map(billPayer => billPayer.billPayerId);

                    return [
                        BillPayerActions.fetchAllSuccess.action({billPayers: List(result.billPayers)}),
                        ChildActions.fetchAllSuccess.action(List(result.children)),
                        Actions.fetchDataSuccess.action({billPayerIds}),
                        Actions.fetchBalanceData.action({
                            institutionId,
                            asOf,
                            includeCreditNotesAndCancelledInvoicesInBalance,
                            billPayerIds,
                        }),
                    ];
                }),
            );
        }),
    );

const BALANCE_PAGE_SIZE = 8;
const MAX_CONCURRENT_BALANCE_FETCHES = 6;

/**
 * Fetches balance data for the provided bill payers by chunking the data in chunks of size BALANCE_PAGE_SIZE
 */
const fetchBalanceData = action$ =>
    action$
        .ofType(Actions.fetchBalanceData.type)
        .flatMap(({payload}: ReturnType<typeof Actions.fetchBalanceData.action>) => {
            const {
                institutionId,
                asOf,
                includeCreditNotesAndCancelledInvoicesInBalance,
                billPayerIds,
                expectedPaymentIds,
                unexpectedPaymentIds,
                expectedInvoiceIds,
                unexpectedInvoiceIds,
            } = payload;

            return of(...chunkArray(billPayerIds, BALANCE_PAGE_SIZE)).pipe(
                map(chunk => {
                    return defer(() => {
                        return Observable.from(
                            Api.fetchBalanceInformationForBillPayers(
                                institutionId,
                                asOf,
                                includeCreditNotesAndCancelledInvoicesInBalance,
                                chunk,
                                expectedPaymentIds,
                                unexpectedPaymentIds,
                                expectedInvoiceIds,
                                unexpectedInvoiceIds,
                            ),
                        ).pipe(
                            flatMap(result => {
                                return [Actions.fetchBalanceDataSuccess.action(result)];
                            }),
                        );
                    });
                }),
            );
        })
        // Maximum four simultaneous fetches
        .concurrentQueues(MAX_CONCURRENT_BALANCE_FETCHES, PARALLEL_QUEUE_STRATEGY.LOAD_BALANCING);

export default combineEpics(listenForUpdates, fetchOverviewData, fetchData, fetchBalanceData);
